/**
 * @lines98/client/screens/replay-teaser-screen — teaser surface for OTHER
 * players' replays of TODAY's daily, before rollover.
 *
 * Foundational invariant: Anti-shadow-practice (docs/decisions/DECISIONS.md +
 * docs/specs/anti-cheat.md §3A + docs/specs/replays-ghosts.md §3A.3). This
 * surface NEVER shows visual artefacts of the game (no board, no moves[], no
 * final frame, no placeholder/blur — even an outline would leak position
 * information). The DOM literally has NO board element.
 *
 * Server-side enforcement (T-9): the `/r/<short_id>` endpoint decides
 * PLAYABLE vs TEASER vs 404 based on §3A.1 three-branch authorization. Client
 * renders whichever shape the server returned. There is NO client-side guard
 * («if today's daily, show teaser») — that would be DevTools-bypassable.
 *
 * Data source (§A.2): the real `/api/replay/<short_id>` TEASER branch, mapped to
 * `TeaserData` by `app.ts` from `replay-api.getReplay`. The screen itself stays a
 * pure view — it accepts `TeaserData` and never fetches (mock `?demo=teaser`
 * mounts use the same signature for viewport tests + visual baselines).
 *
 * Дискета «Сохранить в Книгу» (тикет B AC-B7, fix-цикл 2026-06-24): сохранять ЧУЖУЮ
 * сегодняшнюю daily РАЗРЕШЕНО — закладка создаётся сразу (занимает слот), playable-показ
 * гейтится §3A до rollover (открытие из Книги = ТИЗЕР). Значит дискета обязана быть и на
 * ТИЗЕРЕ чужой партии, не только на playable-плеере. Экран ПЕРЕИСПОЛЬЗУЕТ ОБЩИЙ kit
 * `lib/replay-save-button` (НЕ копию) через тот же generic-слот `.replay-save-slot`, что
 * у плеера (единый источник book-API/овершлоу-попапа/позиционирования). Опция `foreignSave`
 * задаётся ТОЛЬКО для ЧУЖОГО тизера (решение «чужой/свой» — серверное, как у плеера); на
 * своём тизере отсутствует → дискеты нет (own_game_not_bookmarkable).
 *
 * Spec: docs/specs/ui-surfaces-t-7.3.md §5.3.2 + docs/specs/replays-ghosts.md
 * §3A.3 (foundational invariant Anti-shadow-practice) + спека §6.4 / ADR-0023 §Q1
 * (дискета на тизере чужой сегодняшней daily).
 *
 * Layout-plan + CSS: packages/client/src/styles/replay.css `.replay-screen--teaser`
 * block (mobile portrait stacked / mobile landscape split countdown L /
 * desktop stacked card); дискета в `.teaser-title-row` (= шапка тизера, ВНЕ потока
 * KPI/CTA — тот же приём изоляции от 2D-коллизий, что `.replay-title-row` у плеера).
 */

import { ACTION_RAIL_FILL } from '../lib/action-rail.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import { t } from '../lib/i18n/index.js';
import { attachReplaySaveButton, type ReplaySaveButtonHandle } from '../lib/replay-save-button.js';
import { attachTitleFit } from '../lib/title-fit.js';
import { FS_BUTTON_MIN_PX } from '../lib/viewport-sizer.js';
import {
  formatHms,
  msUntilNextRollover,
  startCountdown,
  type CountdownHandle,
} from '../services/countdown.js';
import type { ScreenHandle } from './board-screen.js';
import type { ForeignSaveOptions } from './replay-screen.js';

/**
 * Teaser view-model. Built from the SERVER's TEASER response
 * (`replay-api.getReplay`, §A.2) OR from mock data (`?demo=teaser`).
 *
 * NOTE: NO `moves[]` / `seed` / `final_grid` fields — foundational invariant
 * (replays-ghosts.md §3A.3) requires these be absent from the wire to TEASER
 * clients. The type itself enforces this — DOM literally cannot render them.
 *
 * `playerName` is OPTIONAL: the current `/api/replay` teaser response does NOT
 * carry a player name (the `players` table has no display_name yet — ADR-0008,
 * future; server-contract gap flagged in the PR — spec §3A.3 lists name as
 * teaser content). When absent, a neutral «Игрок» label renders so the
 * `.teaser-player-name` slot stays present (layout + must-visible).
 *
 * `rank` is nullable (server returns null for an unranked attempt — shown as «—»).
 *
 * `countdownToRolloverSeconds` is the server-authoritative remaining seconds at
 * fetch. The live ticker re-derives the wall-clock countdown each second via the
 * SHARED `msUntilNextRollover` (single product-wide rollover math — same boundary
 * the server uses); the server value is the trustworthy first-paint offset.
 */
export interface TeaserData {
  readonly playerName?: string;
  readonly scoreVerified: number;
  readonly rank: number | null;
  readonly movesCount: number;
  /** Server-authoritative remaining seconds at fetch (optional for mocks). */
  readonly countdownToRolloverSeconds?: number;
}

/**
 * First-paint countdown text. Prefers the server-authoritative remaining seconds
 * (avoids a clock-skew flash before the first tick); falls back to wall-clock
 * rollover math via the SHARED helper.
 */
function formatCountdown(serverSeconds?: number): string {
  const ms =
    serverSeconds !== undefined && Number.isFinite(serverSeconds)
      ? Math.max(0, serverSeconds) * 1000
      : msUntilNextRollover(Date.now());
  return formatHms(ms);
}

export function mountReplayTeaserScreen(
  host: HTMLElement,
  data: TeaserData,
  onPlayDaily?: () => void,
  /**
   * Дискета «Сохранить в Книгу» (AC-B7) — задаётся ТОЛЬКО для ЧУЖОГО тизера. Когда задана —
   * общий kit `lib/replay-save-button` монтируется в шапку тизера (`.replay-save-slot`,
   * ВНЕ потока KPI/CTA — AC-B5). Отсутствует → дискеты нет (свой тизер / fallback).
   */
  foreignSave?: ForeignSaveOptions,
): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'replay-screen replay-screen--teaser screen-root';
  root.innerHTML = template(data);
  host.appendChild(root);

  // Server-authoritative first paint, then the live wall-clock ticker takes over
  // (both use the SHARED rollover math; the server value just avoids a skew flash).

  // Заголовок — ОБЩИЙ модуль lib/title-fit (единый размер на всех экранах).
  const titleFit = attachTitleFit(root);

  // Кнопки CTA — group-uniform autofit (line + floor + capToDesign), как у других
  // экранов (result/daily action-rail). Тизер — ВСЕГДА stacked-centered (board-less),
  // поэтому одна цель без onlyLayout. Без этого длинный i18n-CTA (pt «Jogar tabuleiro
  // diário» / «Tabuleiro diário») вылезал за кнопку (.btn nowrap) на узких вьюпортах —
  // h-clip 93>90. На floor-шрифте лейбл переносится ПО СЛОВАМ (CSS white-space:normal
  // ниже), а не уходит под floor / режется (skill §0.A P1, §8.10 B/C). uniform — равный
  // размер обеих кнопок (CTA + «На главную»).
  const btnFit: AutofitHandle = attachAutofit(root, [
    {
      selector: '.teaser-actions .btn',
      mode: 'line',
      uniform: true,
      fillRatio: ACTION_RAIL_FILL,
      capToDesign: true,
      minPx: FS_BUTTON_MIN_PX,
    },
  ]);

  // Live countdown — updates .teaser-countdown текст каждую секунду.
  const cdEl = root.querySelector<HTMLElement>('#teaser-countdown');
  let countdown: CountdownHandle | null = null;
  if (cdEl) {
    countdown = startCountdown((hms) => {
      cdEl.textContent = hms;
    });
  }

  // «?» убран (owner-directive 2026-05-30): описание идёт строкой над таймером.

  // Wire the «Играть» CTA.
  const ctaBtn = root.querySelector<HTMLButtonElement>('#teaser-cta');
  if (ctaBtn && onPlayDaily) {
    ctaBtn.addEventListener('click', () => {
      onPlayDaily();
    });
  }
  // Secondary back-link → главный экран. Owner-feedback 2026-05-26: «нужна ещё
  // кнопка, которая выводит в основную игру». Помещён subtle text-link, не
  // primary CTA, чтобы не конкурировать с основным «Сыграть».
  const backBtn = root.querySelector<HTMLButtonElement>('#teaser-back');
  if (backBtn) {
    backBtn.addEventListener('click', () => {
      window.location.assign('/');
    });
  }

  // Дискета «Сохранить в Книгу» (AC-B7) — ТОЛЬКО для ЧУЖОГО тизера (опция задана). Общий
  // kit lib/replay-save-button (icon-button + book-api + anchored-popover овершлоу), тот же
  // generic-слот `.replay-save-slot`, что у плеера (единый источник — НЕ копия). На своём
  // тизере опция отсутствует → дискеты нет (own_game_not_bookmarkable, AC-B1).
  let saveButton: ReplaySaveButtonHandle | null = null;
  if (foreignSave) {
    const slot = root.querySelector<HTMLElement>('#teaser-save-slot');
    if (slot) {
      saveButton = attachReplaySaveButton(foreignSave);
      slot.appendChild(saveButton.element);
    }
  }

  return {
    unmount(): void {
      if (countdown) countdown.stop();
      titleFit.detach();
      btnFit.detach();
      saveButton?.destroy();
      host.innerHTML = '';
    },
  };
}

function template(data: TeaserData): string {
  // owner-directive 2026-05-30: «?» убран (описание — строкой); таймер — голое
  // число без чипа/подписи (подпись = строка над ним); карточка игрока крупнее и
  // единым стилем; единый шрифт (--fs-control) на значениях карточки + таймере +
  // кнопках (гармония); 2 кнопки по размеру как casual, короткий CTA.
  return `
    <div class="teaser-title-row">
      <h2 class="teaser-title screen-title">${escapeHtml(t('game.teaser.title'))}</h2>
      <!-- Слот дискеты «Сохранить в Книгу» (AC-B7): ВНЕ потока KPI/CTA (тот же приём
           изоляции от 2D-коллизий, что .replay-title-row у плеера). Generic-класс
           .replay-save-slot = единый источник позиционирования; kit монтируется сюда
           ТОЛЬКО для чужого тизера, на своём слот пуст. -->
      <div class="replay-save-slot" id="teaser-save-slot"></div>
    </div>
    <p class="teaser-lead">${escapeHtml(t('game.teaser.lead'))}</p>
    <div class="teaser-timer" id="teaser-countdown">${formatCountdown(data.countdownToRolloverSeconds)}</div>

    <div class="teaser-card">
      <div class="teaser-player">
        <span class="teaser-player-avatar" aria-hidden="true"></span>
        <span class="teaser-player-name">${escapeHtml(data.playerName ?? t('game.teaser.anonPlayer'))}</span>
      </div>
      <div class="teaser-kpis">
        <div class="teaser-kpi"><span class="teaser-kpi-v">${data.rank !== null ? `#${data.rank}` : '—'}</span><span class="teaser-kpi-k">${escapeHtml(t('game.teaser.kpiPlace'))}</span></div>
        <div class="teaser-kpi"><span class="teaser-kpi-v">${formatNumber(data.scoreVerified)}</span><span class="teaser-kpi-k">${escapeHtml(t('game.teaser.kpiScore'))}</span></div>
        <div class="teaser-kpi"><span class="teaser-kpi-v">${data.movesCount}</span><span class="teaser-kpi-k">${escapeHtml(t('game.teaser.kpiMoves'))}</span></div>
      </div>
    </div>

    <div class="teaser-actions">
      <button class="btn primary" id="teaser-cta" type="button">${escapeHtml(t('game.teaser.ctaDaily'))}</button>
      <button class="btn" id="teaser-back" type="button">${escapeHtml(t('game.teaser.ctaHome'))}</button>
    </div>
  `;
}

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&'
      ? '&amp;'
      : ch === '<'
        ? '&lt;'
        : ch === '>'
          ? '&gt;'
          : '&quot;',
  );
}
